/**
 * Created by PRIME AI.
 * @author Samuel Hinchliffe <samuel@primeai.co.uk, sam.hinchliffe.work@gmail.com>
 * @see    [Linkedin] {@link https://www.linkedin.com/in/samuel-hinchliffe-2bb5801a5/}
 *
 * @summary The AppFactory is a stateless library designed to run PRIME AIs web apps.
 * Where we perform our most common tasks before running an application. Like getting
 * retailer details, product details etc. All of which is required to run a PRIME AI App.
 *
 * The AppFactory follows the structure of the common configuration format. See FCBP Widget (The first)
 * This will typically be extended by the AppRunner that will use all of these functions to create
 * and run PrimeAI Apps.
 *
 * Created at: 25/10/2023
 * @see       [PRIME-AI GITHUB] {@link https://github.com/PRIME-AI-GITHUB}
 * @see       [Website]         {@link https://www.primeai.co.uk/}
 * @copyright 2018-2023 PRIME AI LTD
*/
export default class AppFactory {
    constructor({
        parentId, retailerId, productId, userDevice, masterConfig, isPdpPage,
    }) {
        this.parentId = parentId;
        this.retailerId = retailerId;
        this.productId = productId;
        this.masterConfig = masterConfig;
        this.userDevice = userDevice;
        this.hostPath = `${this.masterConfig.ENVIRONMENT.PATH}`;
        this.productDetails = null;
        this.config = null;
        this.isPdpPage = isPdpPage;

        // Insert / Override functions for AppFactory
        // This will store the original functions in-case required.
        this.overrides = {};
        this.applyOverrides();
    }

    // !! Abstract out to the AppManager Level.
    async isAllowedToRun() {
        // Disabled by hand?
        if (!this.config.enabled) {
            return { result: false, reason: 'App Runner will not run. Config has disabled it.' };
        }
        return { result: true, reason: 'All Good' };
    }

    async getProduct(productId) {
        const formData = new FormData();
        formData.append('prime_id', this.retailerId);
        formData.append('product_id', productId);
        formData.append('cache', this.config.useCache);

        try {
            const response = await fetch(this.config.urls.apis.get_product, { method: 'POST', body: formData, redirect: 'follow' });
            const result = await response.json();
            return result.response.response_object;
        } catch (error) {
            console.error('error', error);
        }
        return false;
    }

    async loadAllPromises(urls) {
        const promises = urls.map((url) => {
            url = this.resolveURLs(url);
            // Check if the URL starts with a dot (relative URL)
            return this.loadFile(url);
        });

        return Promise.all(promises);
    }

    resolveURLs(input) {
        if (typeof input === 'string') {
            // If a single string URL is provided, resolve it
            if (input.startsWith('./')) {
            // Concatenate this.hostPath with the relative URL
                return `${this.hostPath}/${input.slice(2)}`;
            }
            return input; // Return the URL as is
        } if (Array.isArray(input)) {
            // If an array of URLs is provided, resolve each URL
            return input.map((url) => {
                if (url.startsWith('./')) {
                    // Concatenate this.hostPath with the relative URL
                    return `${this.hostPath}/${url.slice(2)}`;
                }
                return url; // Return the URL as is
            });
        } if (typeof input === 'object') {
            // If an object with keys and URLs is provided, resolve the keys and leave the URLs as is
            const resolvedObject = {};
            for (const key in input) {
                if (input[key].startsWith('./')) {
                    resolvedObject[`${key}`] = `${this.hostPath}/${input[key].slice(2)}`;
                } else {
                    resolvedObject[key] = input[key];
                }
            }
            return resolvedObject;
        }
        // Handle other cases, e.g., non-string, non-array, non-object inputs
        return input;
    }

    // !! UPDATE AppFactory Compiler to link 'this' as it's parent owner.
    async compileApp(appKey, parentKey) {
        // Download all files related to the app.
        // Using non-io blocking techniques
        // (Download all at once, no script is dependent on another)
        const stylePromises = this.loadAllPromises(appKey.content.styles);
        const scriptPromises = this.loadAllPromises(appKey.content.scripts);
        const markupPromises = this.loadAllPromises(appKey.content.markup);

        // This one can be override by the script tag.
        const overrideLanguage = AppFactory.getLanguageCode();
        if (overrideLanguage) {
            console.log('🤖🤖 PRIME AI Overriding language to:', overrideLanguage);
            appKey.content.language[0] = `./content/langs/${overrideLanguage}.json`;
        }
        const languagePromises = this.loadAllPromises(appKey.content.language);

        // ? For a future feature, allow for bundled content, no download needed.

        // Wait until all are downloaded
        let [styles, scripts, markup, languageString] = await Promise.all([stylePromises, scriptPromises, markupPromises, languagePromises]);

        // Resolve static contents URLs. Is it relative to PRIME AI?
        const resources = this.resolveURLs(this.config.urls.resources);

        // Resolve links within the markup
        markup = markup.map((html) => this.resolveHtmlUrls(html));

        // Files now all loaded, initialise the classes and pass their requirements.
        // And resolve the urls within them.
        let element;

        if (appKey.shadowRootDisabled) {
            element = this.createElement(appKey.element_name);
        } else {
            element = this.createShadowRootedElement(appKey.element_name);
        }

        // Get the details to the mounting of the element and it's copies.
        const mountPoints = appKey.elements;

        // App Factory reference
        const appFactory = this;

        // Language
        const language = JSON.parse(languageString);
        const languageCode = this.config.language;

        // Create the product Pages object.
        const productPageInfo = {
            parentId: this.parentId,
            retailerId: this.retailerId,
            productId: this.productId,
            masterConfig: this.masterConfig,
            retailerConfig: this.config,
            userDevice: this.userDevice,
            languageCode,
        };

        const childApps = {};
        const parent = parentKey;

        // !! BEWARE OF THE ASYNC AWAIT HERE, HOW QUICK YOU NEED IT?
        if (appKey.child_apps?.length) {
            for (const app of appKey.child_apps) {
                if (this.config.apps[app]) {
                    const compiledApp = await this.compileApp(this.config.apps[app], appKey);
                    childApps[app] = compiledApp;
                } else {
                    console.warn(`App key "${app}" not found in config.apps`);
                    throw new Error('App not found!');
                }
            }
        }

        // Pass details to their expected class.
        return {
            styles,
            scripts,
            element,
            markup,
            resources,
            childApps,
            parent,
            language,
            languageCode,
            mountPoints,
            product: this.productDetails,
            productPageInfo,
            appFactory,
            analytics: this.analytics,
        };
    }

    createElement(tagName) {
        const element = document.createElement(tagName);
        return element;
    }

    createShadowRootedElement(tagName) {
        // Create a new element with the specified tag name
        const element = document.createElement(tagName);

        // Attach a shadow DOM to the newly created element
        element.attachShadow({ mode: 'open' });

        // Return the element with its shadow DOM attached
        return element;
    }

    static getLanguageCode() {
        const languageCode = document.querySelector('script[prime-ai-script]').getAttribute('language-code');
        if (languageCode) {
            return languageCode.toLowerCase();
        }
        return null;
    }

    static getParentRetailerId() {
        return document.querySelector('script[prime-ai-script]').getAttribute('parent-id');
    }

    static getChildRetailerId() {
        return document.querySelector('script[prime-ai-script]').getAttribute('child-id');
    }

    static getProductIdOverride() {
        return document.querySelector('script[prime-ai-script]').getAttribute('product-id');
    }

    static async getProductId(isPdpPage) {
        const possibleProductId = AppFactory.getProductIdOverride();
        if (possibleProductId) return possibleProductId;

        if (!isPdpPage) return 'Not a PDP page';
        await AppFactory.waitForVariable('meta');

        // You are not on a PDP.
        if (!window.meta.product) {
            return 'NoProduct';
        }

        const { sku } = window.meta.product.variants[0];
        const lastIndex = sku.lastIndexOf('-');
        const productId = sku.substring(0, lastIndex);

        if (!productId) {
            throw new Error('No Product ID was extracted from meta var');
        }

        return productId;
    }

    static isPdpPage() {
        // Consider changing this, It's not unknown for products to be
        // previewed on a page that isn't a PDP.
        const isPDPPage = window.location.pathname.includes('/products/');

        // Return true if it's a PDP page based on the URL pattern
        if (isPDPPage) return true;
        return false;
    }

    async loadConfig(path) {
        const response = await fetch(path);

        if (!response.ok) {
            throw new Error(`Failed to load config from: HTTP status ${response.status}`);
        }

        const config = await response.json();
        this.config = config;
        return config;
    }

    async loadFile(url) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch ${url}. Status: ${response.status}`);
            }
            return response.text();
        } catch (error) {
            throw error;
        }
    }

    resolveHtmlUrls(htmlString) {
        const srcRegex = /src="([^"]*)"/g;
        const hrefRegex = /href="([^"]*)"/g;

        let match;
        let resolvedHtml = htmlString;

        match = srcRegex.exec(htmlString);
        while (match) {
            const originalSrc = match[0];
            const srcValue = match[1];
            const resolvedSrc = this.resolveURLs(srcValue);
            resolvedHtml = resolvedHtml.replace(originalSrc, `src="${resolvedSrc}"`);
            match = srcRegex.exec(htmlString);
        }

        match = hrefRegex.exec(htmlString);
        while (match) {
            const originalHref = match[0];
            const hrefValue = match[1];
            const resolvedHref = this.resolveURLs(hrefValue);
            resolvedHtml = resolvedHtml.replace(originalHref, `href="${resolvedHref}"`);
            match = hrefRegex.exec(htmlString);
        }

        return resolvedHtml;
    }

    static async waitForVariable(variableName) {
        return new Promise((resolve) => {
            const checkVariable = () => {
                if (window[variableName] !== undefined) {
                    resolve(window[variableName]);
                } else {
                    console.warn(`${variableName} NOT FOUND! - Cannot find , check again in 100ms`);
                    setTimeout(checkVariable, 100); // Check again in 100 milliseconds
                }
            };

            checkVariable();
        });
    }

    // static log(message, level = 0) {
    //     if (!window.__PRIME_AI__.debugMode) return false;
    //     console.log(message);
    //     // const error = new Error(); // Create an Error object to capture the stack trace
    //     // const stack = error.stack.split('\n')[2].trim(); // Extract the caller from the stack trace

    //     // let color = '#35495e'; // Default to blue (level 0)

    //     // switch (level) {
    //     // case 1:
    //     //     color = '#0F9D58'; // Green (important info)
    //     //     break;
    //     // case 2:
    //     //     color = '#FFA700'; // Yellow (warning)
    //     //     break;
    //     // case 3:
    //     //     color = '#DB4437'; // Red (error)
    //     //     break;
    //     // default:
    //     //     color = '#4285F4'; // Blue (log)
    //     // }

    //     // console.log(
    //     //     // eslint-disable-next-line no-useless-concat
    //     //     `%c PRIME AI FCBP %c ${this.constructor.name} %c ${message} %c ${stack}`,
    //     //     'background:#35495e; padding: 1px; border-radius: 3px 0 0 3px;  color: #fff',
    //     //     'background:#FF5733; padding: 1px; border-radius: 0 0 0 0;  color: #fff',
    //     //     `background:${color}; padding: 1px; border-radius: 0 3px 3px 0;  color: #fff`,
    //     //     'background:transparent',
    //     // );
    // }

    static getUserDevice() {
        const screenWidth = window.innerWidth;
        let device;

        switch (true) {
        case screenWidth >= 992:
            device = 'desktop';
            break;
        case screenWidth >= 768:
            device = 'tablet';
            break;
        default:
            device = 'mobile';
            break;
        }

        return device;
    }

    overrideMethod(name, fn) {
        this.overrides[name] = fn;
        this[name] = fn;
    }

    restoreMethod(name) {
        if (name in this.overrides) {
            this[name] = this.overrides[name];
            delete this.overrides[name];
        }
    }

    applyOverrides() {
        // Check if overrides exist for appFactory
        if (window.__PRIME_AI__.override[this.constructor.name]) {
            const appFactoryOverrides = window.__PRIME_AI__.override[this.constructor.name];
            for (const methodName in appFactoryOverrides) {
                if (appFactoryOverrides.hasOwnProperty(methodName)) {
                    this.overrideMethod(methodName, appFactoryOverrides[methodName]);
                }
            }
        }
    }
}
